import { CalendarGridColumnName } from '../../../grid/types/column.types';
import { EnhancementDealLevelFilter } from '../../../grid/utils/filter/ratings/enhancement-deal-level-filter';
import { RatingDealLevelFilter } from '../../../grid/utils/filter/ratings/ratings-deal-level-filter';
import {
  PredefinedEnhancementFilterValue,
  RATINGS,
  RatingsFilterValue,
  getEnhancementFilterOptionLabel,
  getRatingFilterOptionLabel
} from '../../../grid/utils/filter/ratings/ratings-filters';
import {
  FilterChange,
  FilterClear
} from '../../../state/calendar-filter/actions';
import {
  CalendarFilter,
  CalendarFiltersState
} from '../../../state/calendar-filter/state';
import { FilterResultFacade } from '../filter-result-facade';
import {
  AppliedFilterItem,
  ConfigureOptionsParams,
  FilterOptions,
  FilterPassParams,
  IFilterComparator,
  RatingsFilterModel
} from '../filter-types';
import { FilterHandlerBase } from './base/filter-handler-base';
import { RatingsFilterModelComparator } from './comparators/ratings-filter-model-comparator';

export class RatingsFilterHandler extends FilterHandlerBase {
  private readonly RATINGS_LABEL = 'Ratings';
  private readonly ENCHANCEMENT_LABEL = 'Enhancement';
  private comparator: IFilterComparator<RatingsFilterModel>;

  get name(): string {
    return CalendarGridColumnName.Ratings;
  }

  get isInclusiveExclusive(): boolean {
    return false;
  }

  configureFilterOptions(params: ConfigureOptionsParams) {
    const enhancements = [...params.filterOptions.enhancements].sort(
      (left, right) => left.localeCompare(right)
    );

    const colDef = params.api
      .getColumnDefs()
      ?.find(
        (c: any) =>
          c.field === CalendarGridColumnName.Ratings ||
          c.colId === CalendarGridColumnName.Ratings
      ) as any;
    if (colDef?.filterParams) {
      if (!colDef.filterParams.filterOptions) {
        colDef.filterParams.filterOptions = {};
      }
      colDef.filterParams.filterOptions.enhancements = enhancements;
    }

    params.api
      .getColumnFilterInstance(CalendarGridColumnName.Ratings)
      .then((filterInstance: any) => {
        const componentInstance =
          filterInstance?.getFrameworkComponentInstance?.() ||
          filterInstance?._frameworkComponentInstance ||
          filterInstance;

        componentInstance?.updateEnhancements?.(enhancements);
      });
  }

  getFilterOptions(filterOptions: FilterOptions): any {
    return filterOptions.enhancements;
  }

  hasValidDiff(options: FilterOptions, grid: any, store: any): boolean {
    return this.getValidDiffComparator(options).hasValidDiff(grid, store);
  }

  getValidDiffComparator(
    options: FilterOptions
  ): IFilterComparator<RatingsFilterModel> {
    if (!this.comparator) {
      this.comparator = new RatingsFilterModelComparator();
    }
    this.comparator.applyFilterOptions(this.getFilterOptions(options));
    return this.comparator;
  }

  doFilterPass(params: FilterPassParams, result: FilterResultFacade): boolean {
    const model = params.filters[this.name].model;
    const deal = params.deal;

    const enhancementFilter = new EnhancementDealLevelFilter(model, result);
    const ratingsFilter = new RatingDealLevelFilter(model, result, this.name);

    return (
      enhancementFilter.applyFilter(deal) && ratingsFilter.applyFilter(deal)
    );
  }

  getAppliedFiltersItems(filter: CalendarFilter): AppliedFilterItem[] {
    if (!!filter && !!filter.model) {
      const result = [];
      const newItem: (arg0, arg1, arg2) => unknown =
        this.createAppliedFilterItem;
      if (filter.model.ratingsRadio === RatingsFilterValue.notRated) {
        result.push(
          newItem(
            filter,
            this.RATINGS_LABEL,
            getRatingFilterOptionLabel(RatingsFilterValue.notRated)
          )
        );
      } else {
        const ratingsValue = `${RATINGS[filter.model.minValue]} thru ${
          RATINGS[filter.model.maxValue]
        }`;
        switch (filter.model.radio) {
          case PredefinedEnhancementFilterValue.all:
            result.push(newItem(filter, this.RATINGS_LABEL, ratingsValue));
            break;
          case PredefinedEnhancementFilterValue.enhanced:
            result.push(
              newItem(
                filter,
                this.ENCHANCEMENT_LABEL,
                getEnhancementFilterOptionLabel(
                  PredefinedEnhancementFilterValue.enhanced
                )
              )
            );
            break;
          case PredefinedEnhancementFilterValue.unenhanced:
            result.push(newItem(filter, this.RATINGS_LABEL, ratingsValue));
            result.push(
              newItem(
                filter,
                this.ENCHANCEMENT_LABEL,
                getEnhancementFilterOptionLabel(
                  PredefinedEnhancementFilterValue.unenhanced
                )
              )
            );
            break;
          default:
            result.push(
              this.createAppliedFilterItem(
                filter,
                this.ENCHANCEMENT_LABEL,
                filter.model.radio
              )
            );
        }
      }
      return result;
    }
    return [];
  }

  createAppliedFilterItem(
    filter: CalendarFilter,
    label: string,
    value: string
  ): AppliedFilterItem {
    return {
      name: filter.name,
      label: label,
      filterLabel: label,
      filterValue: value
    };
  }

  filterClear(
    filterChange: FilterClear,
    state: CalendarFiltersState
  ): FilterChange {
    const result = { name: this.name, value: undefined };
    if (filterChange.name === this.name) {
      const filterName = filterChange.name;
      const subFilterName = filterChange.subFilterName;
      const filter = { ...state[filterName] };
      if (subFilterName === this.RATINGS_LABEL) {
        filter.model.ratingsRadio = undefined;
      } else if (subFilterName === this.ENCHANCEMENT_LABEL) {
        switch (filter.model.radio) {
          case PredefinedEnhancementFilterValue.all:
            filter.model.ratingsRadio = undefined;
            filter.model.radio = undefined;
            break;
          case PredefinedEnhancementFilterValue.enhanced:
            filter.model.ratingsRadio = undefined;
            filter.model.radio = undefined;
            break;
          case PredefinedEnhancementFilterValue.unenhanced:
            if (
              filter.model.minValue === 0 &&
              filter.model.maxValue === RATINGS.length - 1
            ) {
              filter.model.radio = undefined;
              filter.model.ratingsRadio = undefined;
            } else {
              const ratingsValue = `${RATINGS[filter.model.minValue]} thru ${
                RATINGS[filter.model.maxValue]
              }`;
              filter.model.stringModel = ratingsValue;
              filter.model.radio = PredefinedEnhancementFilterValue.all;
              filter.model.ratingsRadio = RatingsFilterValue.all;
              filter.timestamp = Date.now();
            }
            break;
          default:
            filter.model.ratingsRadio = undefined;
            filter.model.radio = undefined;
        }
      } else {
        filter.model.ratingsRadio = undefined;
        filter.model.radio = undefined;
      }
      result.value =
        filter.model.ratingsRadio === undefined &&
        filter.model.radio === undefined
          ? undefined
          : filter;
    }
    return result;
  }
}
